/**
 * VIP/단골 관리 컴포넌트
 * DDD 리팩토링 완료 - 도메인 로직 분리
 */
import React from 'react';
import {
  StarIcon as CrownIcon,
  StarIcon,
  ShieldCheckIcon,
  GiftIcon,
  ChartBarIcon,
  UserGroupIcon,
  CurrencyDollarIcon,
  PlusIcon,
  PencilIcon,
  EyeIcon,
  ArrowPathIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
} from '@heroicons/react/24/outline';
import { useVipManagement } from '../use-cases';
import { vipManagementService } from '../services';

const VipManagement: React.FC = () => {
  const {
    loading,
    error,
    activeTab,
    setActiveTab,
    members,
    searchTerm,
    setSearchTerm,
    filterTier,
    setFilterTier,
    sortBy,
    sortOrder,
    handleSortChange,
    viewMemberDetail,
    openPointAdjust,
    tierConfigs,
    openTierEdit,
    statistics,
    refreshCurrentTab,
  } = useVipManagement();

  // 등급별 아이콘
  const getTierIcon = (tierLevel: string) => {
    const iconClass = 'h-5 w-5';
    switch (tierLevel) {
      case 'diamond':
        return <CrownIcon className={`${iconClass} text-purple-600`} />;
      case 'platinum':
        return <StarIcon className={`${iconClass} text-gray-600`} />;
      case 'gold':
        return <ShieldCheckIcon className={`${iconClass} text-yellow-600`} />;
      case 'silver':
        return <GiftIcon className={`${iconClass} text-gray-400`} />;
      default:
        return <UserGroupIcon className={`${iconClass} text-amber-600`} />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-600">❌ {error}</p>
            <button
              onClick={() => window.location.reload()}
              className="mt-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
            >
              새로고침
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* 헤더 */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center">
                <CrownIcon className="h-8 w-8 text-yellow-500 mr-3" />
                VIP/단골 관리
              </h1>
              <p className="text-gray-600 mt-2">단골 고객 등급 관리 및 혜택 운영</p>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={refreshCurrentTab}
                className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                <ArrowPathIcon className="h-4 w-4" />
                <span>새로고침</span>
              </button>
            </div>
          </div>
        </div>

        {/* 탭 메뉴 */}
        <div className="mb-6">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab('members')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'members'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <UserGroupIcon className="h-4 w-4 inline mr-2" />
                회원 관리
              </button>
              <button
                onClick={() => setActiveTab('tiers')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'tiers'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <ShieldCheckIcon className="h-4 w-4 inline mr-2" />
                등급 설정
              </button>
              <button
                onClick={() => setActiveTab('statistics')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'statistics'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <ChartBarIcon className="h-4 w-4 inline mr-2" />
                통계 분석
              </button>
            </nav>
          </div>
        </div>

        {/* 회원 관리 탭 */}
        {activeTab === 'members' && (
          <div className="space-y-6">
            {/* 검색 및 필터 */}
            <div className="bg-white p-4 rounded-lg border border-gray-200">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
                <div className="flex flex-1 items-center space-x-4">
                  {/* 검색 */}
                  <div className="relative flex-1 max-w-md">
                    <MagnifyingGlassIcon className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="회원 검색..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  {/* 등급 필터 */}
                  <select
                    value={filterTier}
                    onChange={(e) => setFilterTier(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">모든 등급</option>
                    <option value="bronze">브론즈</option>
                    <option value="silver">실버</option>
                    <option value="gold">골드</option>
                    <option value="platinum">플래티넘</option>
                    <option value="diamond">다이아몬드</option>
                  </select>
                </div>

                {/* 정렬 */}
                <div className="flex items-center space-x-2">
                  <FunnelIcon className="h-4 w-4 text-gray-400" />
                  <select
                    value={vipManagementService.createSortOptionValue(sortBy, sortOrder)}
                    onChange={(e) => handleSortChange(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="total_spent-desc">총 지출액 높은 순</option>
                    <option value="total_spent-asc">총 지출액 낮은 순</option>
                    <option value="total_visits-desc">방문 횟수 많은 순</option>
                    <option value="total_visits-asc">방문 횟수 적은 순</option>
                    <option value="last_visit_date-desc">최근 방문 순</option>
                    <option value="tier_level-desc">등급 높은 순</option>
                  </select>
                </div>
              </div>
            </div>

            {/* 회원 목록 */}
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        회원
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        등급
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        포인트
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        방문/지출
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        최근 방문
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        작업
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {members.map((member) => (
                      <tr key={member.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10">
                              <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                                <UserGroupIcon className="h-5 w-5 text-gray-600" />
                              </div>
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">회원 #{member.user_id}</div>
                              <div className="text-sm text-gray-500">
                                {vipManagementService.formatDate(member.membership_start_date)} 가입
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            {getTierIcon(member.tier_level)}
                            <span
                              className={`ml-2 px-2 py-1 text-xs font-semibold rounded-full border ${vipManagementService.getTierColor(
                                member.tier_level
                              )}`}
                            >
                              {member.tier_name}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{member.available_points.toLocaleString()}P</div>
                          <div className="text-sm text-gray-500">(총 {member.lifetime_points.toLocaleString()}P)</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{member.total_visits}회 방문</div>
                          <div className="text-sm text-gray-500">
                            {vipManagementService.formatCurrency(member.total_spent)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {member.last_visit_date ? vipManagementService.formatDate(member.last_visit_date) : '미방문'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex items-center space-x-2">
                            <button onClick={() => viewMemberDetail(member)} className="text-blue-600 hover:text-blue-900">
                              <EyeIcon className="h-4 w-4" />
                            </button>
                            <button onClick={() => openPointAdjust(member)} className="text-green-600 hover:text-green-900">
                              <PencilIcon className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* 등급 설정 탭 */}
        {activeTab === 'tiers' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
              {tierConfigs.map((config) => (
                <div key={config.tier_level} className="bg-white rounded-lg border border-gray-200 p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center">
                      {getTierIcon(config.tier_level)}
                      <div className="ml-3">
                        <h3 className="text-lg font-semibold text-gray-900">{config.tier_name}</h3>
                        <p className="text-sm text-gray-500">{config.tier_description}</p>
                      </div>
                    </div>
                    <button onClick={() => openTierEdit(config.tier_level)} className="text-blue-600 hover:text-blue-900">
                      <PencilIcon className="h-4 w-4" />
                    </button>
                  </div>

                  {/* 승급 조건 */}
                  <div className="mb-4">
                    <h4 className="font-medium text-gray-900 mb-2">승급 조건</h4>
                    <div className="space-y-1 text-sm text-gray-600">
                      <div>• 최소 {config.min_visits}회 방문</div>
                      <div>• 최소 {vipManagementService.formatCurrency(config.min_spent)} 지출</div>
                      <div>• 최소 {config.min_months}개월 이용</div>
                    </div>
                  </div>

                  {/* 혜택 */}
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">혜택</h4>
                    <div className="space-y-1 text-sm text-gray-600">
                      <div>• 포인트 {config.point_multiplier}x 적립</div>
                      <div>• {config.discount_rate}% 할인</div>
                      <div>• 월 {config.free_minutes_monthly}분 무료</div>
                      <div>• {config.priority_booking_hours}시간 우선 예약</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 통계 분석 탭 */}
        {activeTab === 'statistics' && statistics && (
          <div className="space-y-6">
            {/* 요약 카드 */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-white p-6 rounded-lg border border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">총 VIP 회원</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {statistics.summary.total_members.toLocaleString()}명
                    </p>
                  </div>
                  <UserGroupIcon className="h-8 w-8 text-blue-600" />
                </div>
              </div>

              <div className="bg-white p-6 rounded-lg border border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">발행 포인트</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {statistics.summary.total_points_issued.toLocaleString()}P
                    </p>
                  </div>
                  <GiftIcon className="h-8 w-8 text-green-600" />
                </div>
              </div>

              <div className="bg-white p-6 rounded-lg border border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">미사용 포인트</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {statistics.summary.total_points_available.toLocaleString()}P
                    </p>
                  </div>
                  <CurrencyDollarIcon className="h-8 w-8 text-orange-600" />
                </div>
              </div>

              <div className="bg-white p-6 rounded-lg border border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">이달 신규 VIP</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {statistics.summary.new_members_this_month.toLocaleString()}명
                    </p>
                  </div>
                  <StarIcon className="h-8 w-8 text-purple-600" />
                </div>
              </div>
            </div>

            {/* 등급별 분포 */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-6">등급별 회원 분포</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 text-sm font-medium text-gray-600">등급</th>
                      <th className="text-left py-3 text-sm font-medium text-gray-600">회원 수</th>
                      <th className="text-left py-3 text-sm font-medium text-gray-600">총 지출액</th>
                      <th className="text-left py-3 text-sm font-medium text-gray-600">평균 방문</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {statistics.tier_distribution.map((tier) => (
                      <tr key={tier.tier_level}>
                        <td className="py-4">
                          <div className="flex items-center">
                            {getTierIcon(tier.tier_level)}
                            <span className="ml-2 capitalize">{tier.tier_level}</span>
                          </div>
                        </td>
                        <td className="py-4 text-gray-900">{tier.member_count.toLocaleString()}명</td>
                        <td className="py-4 text-gray-900">{vipManagementService.formatCurrency(tier.total_spent)}</td>
                        <td className="py-4 text-gray-900">{tier.avg_visits.toFixed(1)}회</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VipManagement;
